/**
 * Copyright 2023-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { theme } from "@nordcloud/gnui";
import {
  ResourceExecutionStatus,
  ResourceGroupStatus,
  ResourceExecutionStatusManual,
} from "~/generated/graphql";
import { IconName } from "~/types/IconName";
import { TooltipColor } from "~/types/TooltipColor";
import type { ResourceWithId } from "./types";

export type StatusConfig = {
  description: string;
  icon: IconName;
  color: () => string;
  tooltipColor: TooltipColor;
  pathColors?: Record<number, string>;
};

type StatusConfigKey =
  | ResourceExecutionStatus
  | ResourceGroupStatus
  | "UNKNOWN";

export const STATUS_CONFIGS: Record<StatusConfigKey, StatusConfig> = {
  [ResourceExecutionStatus.Error]: {
    description: "Error",
    icon: "errorFilled",
    color: () => `${theme.color.background.error}`,
    tooltipColor: TooltipColor.danger,
  },
  [ResourceExecutionStatus.Failed]: {
    description: "Failed",
    icon: "cancelFilled",
    color: () => `${theme.color.background.error}`,
    tooltipColor: TooltipColor.danger,
  },
  [ResourceExecutionStatus.InProgress]: {
    description: "In Progress",
    icon: "inProgressFilled",
    color: () => `${theme.color.background.info}`,
    tooltipColor: TooltipColor.notification,
  },
  [ResourceExecutionStatus.ManualSuccess]: {
    description: "Manually Successful",
    icon: "successManualFilled",
    color: () => `${theme.color.background.success}`,
    tooltipColor: TooltipColor.success,
    pathColors: {
      0: `${theme.color.background.success}`,
      1: "accent",
      2: "white",
    },
  },
  [ResourceExecutionStatus.NotInTimeWindow]: {
    description: "Not In Time Window",
    icon: "timeRangeFilled",
    color: () => `${theme.color.background.warning}`,
    tooltipColor: TooltipColor.warning,
  },
  [ResourceExecutionStatus.Pending]: {
    description: "Pending",
    icon: "pendingFilled",
    color: () => `${theme.color.background.warning}`,
    tooltipColor: TooltipColor.warning,
  },
  [ResourceExecutionStatus.Success]: {
    description: "Successful",
    icon: "successFilled",
    color: () => `${theme.color.background.success}`,
    tooltipColor: TooltipColor.success,
  },
  [ResourceExecutionStatus.Timeout]: {
    description: "Timeout",
    icon: "timeOutFilled",
    color: () => `${theme.color.background.warning}`,
    tooltipColor: TooltipColor.warning,
  },
  [ResourceGroupStatus.PartialSuccess]: {
    description: "Partially Successful",
    icon: "warningFilled",
    color: () => `${theme.color.background.warning}`,
    tooltipColor: TooltipColor.warning,
  },
  ["UNKNOWN"]: {
    description: "Unknown",
    icon: "resource",
    color: () => theme.color.text.text03,
    tooltipColor: TooltipColor.notification,
  },
};

export const SUCCESS_DETAILED_ICON = "statusSuccessDetailed" as IconName;

export function getResourceStatusConfig(
  status?: ResourceExecutionStatus | ResourceGroupStatus | null
): StatusConfig {
  return STATUS_CONFIGS[status ?? "UNKNOWN"];
}

export function getSuccessDetailedStatusConfig(
  statusConfig: StatusConfig
): StatusConfig {
  return {
    ...statusConfig,
    icon: SUCCESS_DETAILED_ICON,
    pathColors: {
      ...(statusConfig.pathColors ?? {}),
      1: theme.color.background.error,
    },
  };
}

export function hasSuccessfulStatus(
  status?: ResourceExecutionStatus | ResourceGroupStatus | null
) {
  return (
    status === ResourceExecutionStatus.Success ||
    status === ResourceGroupStatus.Success
  );
}

export function getAttentionNeededDescription(description: string) {
  return `${description}. Attention needed`;
}

export function getManualResourceStatusLabel(
  status: ResourceExecutionStatusManual
) {
  if (status === ResourceExecutionStatusManual.ManualSuccess) {
    return "Manually Successful";
  }

  return status;
}

export const findDuplicateResourceIds = (
  ...resourceCollections: ResourceWithId[][]
): Set<ResourceWithId["id"]> => {
  const allResources = resourceCollections.flat();

  const initialState = {
    seen: new Set<ResourceWithId["id"]>(),
    duplicates: new Set<ResourceWithId["id"]>(),
  };

  const finalState = allResources.reduce((accumulator, currentResource) => {
    if (accumulator.seen.has(currentResource.id)) {
      accumulator.duplicates.add(currentResource.id);
    } else {
      accumulator.seen.add(currentResource.id);
    }

    return accumulator;
  }, initialState);

  return finalState.duplicates;
};
